// 요금제 값·행 — PLAN_CODES 는 `0090_plan_axis.sql` 의 check 와 한 글자도 다르면 안 된다(migrations 테스트가 맞춰 본다).

import type { OrgScopedRow } from "../common/types";
import type { ProjectStatus } from "../project/types";

export const PLAN_CODES = ["invited", "free", "pro", "max"] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

// 표에 PLAN_CODES 밖 값이 있을 때 — 이름만 모른다고 보이고 한도·유예는 그대로 쓴다(무제한으로 풀지 않는다).
export const UNKNOWN_PLAN = "unknown" as const;
export type PlanName = PlanCode | typeof UNKNOWN_PLAN;

export const PLAN_LABEL: Record<PlanName, string> = {
  invited: "초대",
  free: "무료",
  pro: "Pro",
  max: "Max",
  unknown: "알 수 없음",
};

// 아직 받지 않는 유료 요금제 — 화면은 「준비 중」 으로만 보인다.
export const PREPARING_PLAN_CODES = ["pro", "max"] as const satisfies readonly PlanCode[];

// 셀프 입점 무료 한도 — 사진·첨부 1GB · 알림톡 월 30건(0090 백필 값과 같다 · migrations 테스트가 맞춰 본다).
export const FREE_PLAN_LIMITS = { storage_limit_bytes: 1_073_741_824, notify_month_limit: 30 } as const;

export interface PlanLimits {
  storage_limit_bytes: number | null;
  notify_month_limit: number | null;
}

// 요금제를 바꿀 때 같이 쓰는 한도 열 — 무료만 FREE_PLAN_LIMITS · 초대·Pro·Max·모르는 값은 null(제한 없음 · 0094 RPC 가 그대로 쓴다).
export const planLimitsOf = (plan: string): PlanLimits =>
  plan === "free" ? { ...FREE_PLAN_LIMITS } : { storage_limit_bytes: null, notify_month_limit: null };

// 행이 없는 조직은 초대 기간 조직이다 — 한도 없음.
export const DEFAULT_PLAN_CODE: PlanCode = "invited";

// 과금 축인 「동시 진행 현장」 — 시공중만 센다(시공대기·유지보수·완료 제외).
export const ACTIVE_SITE_STATUSES = ["IN_PROGRESS"] as const satisfies readonly ProjectStatus[];

// 표에서 읽은 그대로 — plan 은 아직 검사 전 문자열.
export interface OrgPlanRow extends OrgScopedRow {
  plan: string;
  active_site_limit: number | null;
  grace_until: string | null;
  // 0090 열 — 열이 생기기 전 행(인메모리 옛 db·운영자 쓰기)은 없을 수 있다 · null·없음 = 제한 없음.
  storage_limit_bytes?: number | null;
  notify_month_limit?: number | null;
}

export interface OrgPlan extends Omit<OrgPlanRow, "plan"> {
  plan: PlanName;
}

// 화면·판정이 쓰는 한 벌 — 행이 없거나 초대면 limit 은 null(제한 없음).
export interface PlanView {
  plan: PlanName;
  limit: number | null;
  graceUntil: string | null;
}

export const isPlanCode = (v: unknown): v is PlanCode =>
  typeof v === "string" && (PLAN_CODES as readonly string[]).includes(v);

// 저장소 두 벌(인메모리·Supabase)이 같이 쓰는 행 변환 — 모르는 플랜 값은 이름만 unknown 으로 바꾼다.
export const toOrgPlan = (row: OrgPlanRow): OrgPlan => ({
  ...row,
  plan: isPlanCode(row.plan) ? row.plan : UNKNOWN_PLAN,
});
